import {Component, inject, OnDestroy, OnInit} from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import {NgxFileDropEntry, NgxFileDropModule} from 'ngx-file-drop';
import { combineLatest, Observable, Subject } from 'rxjs';
import { filter, first, map, startWith, takeUntil } from 'rxjs/operators';
import { StateService } from '../../service/state/state.service';
import { Client } from '../../models/client/client';
import { UserProfile } from '../../models/user/userProfile';
import { HelpRequest, HelpService } from '../../service/help.service';
import { TranslateService } from '@ngx-translate/core';
import {  NavigationEnd, Router } from '@angular/router';
import { animate, state, style, transition, trigger } from "@angular/animations";
import { FullviewIoService } from '../../service/fullview-io.service';
import { ToasterService } from '../../service/toaster.service';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { ReactiveFormsModule } from '@angular/forms';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import {OnClickOutsideDirective} from "../../rma-directives/on-click-outside.directive";

@Component({selector: 'help',
  templateUrl: './help.component.html',
  styleUrls: ['./help.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    ReactiveFormsModule,
    FontAwesomeModule,
    OnClickOutsideDirective,
    NgxFileDropModule,
  ],
  animations: [
    trigger('buttonAnimation', [
      state('hidden', style({
        width: '30px',
        height: '30px',
      })),
      state('visible', style({
        width: '*',
        height: '30px',
      })),
      transition('hidden <=> visible', [
        animate('0.2s'),
      ]),
    ]),
  ],
})
export class HelpComponent implements OnInit, OnDestroy {
  private formBuilder = inject(UntypedFormBuilder);
  private stateService = inject(StateService);
  private helpService = inject(HelpService);
  private translate = inject(TranslateService);
  private toasterService = inject(ToasterService);
  private fullviewIoService = inject(FullviewIoService);

  private router: Router = inject(Router);

  private readonly ATTACHMENT_SIZE = 1048576;
  private readonly ALLOWED_ATTACHMENTS = 3;
  private readonly ERROR_MSG_DISPLAY_TIME = 3000;

  public files: File[] = [];
  public isHelpPanelVisible: boolean = false;
  public feedbackForm: UntypedFormGroup;
  public isErrorMsgVisible: boolean = false;
  public textState = 'hidden';
  public hasFullviewIoAccess$: Observable<boolean>

  private unsubscribe$ = new Subject<void>();
  private currentClient: Client;
  private userProfile: UserProfile;
  public showIcon: Observable<any> = this.router.events
    .pipe(
      filter(event => event instanceof NavigationEnd),
      filter((event: NavigationEnd) => !!event.url),
      map((event: NavigationEnd) => event.url),
      map(url => !url.includes('openReport=true')),
      startWith(true),
    )



  public ngOnInit(): void {
    this.feedbackForm = this.formBuilder.group({
      text: [null, [Validators.required]],
    });

    combineLatest([this.stateService.client$, this.stateService.user$])
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(data => {
        this.currentClient = data[0];
        this.userProfile = data[1];
      });

    this.hasFullviewIoAccess$ = this.stateService.permissions$
      .pipe(
        map(p => p.fullview_io_access),
      );
  }

  public changeHelpPanelVisibility() {
    this.isHelpPanelVisible = !this.isHelpPanelVisible;
  }

  public removeFile(file: File) {
    this.files = this.files.filter(f => f !== file);
  }

  public onFileSelectOrDrop(files: NgxFileDropEntry[]) {
    for (const droppedFile of files) {
      if (droppedFile.fileEntry.isFile) {
        const fileEntry = droppedFile.fileEntry as FileSystemFileEntry;
        fileEntry.file((file: File) => {
          if (file?.size > this.ATTACHMENT_SIZE) {
            this.isErrorMsgVisible = true;
            this.displayErrorMsg();
            return;
          }
          if (this.files?.length < this.ALLOWED_ATTACHMENTS) {
            this.files.push(file);
          }
        });
      }
    }
  }

  public sendHelpRequest() {
    this.helpService.postHelpRequest(this.prepareFeedbackData(), this.files).pipe(
      first())
      .subscribe(() => {
        this.toasterService.pop('success', this.translate.instant('MENU.PORTAL.HELP.TOASTER_MESSAGE'), '');
        this.changeHelpPanelVisibility();
        this.clearHelpWidget();
      });
  }

  public fullviewRequest() {
    this.fullviewIoService.requestSupport();
    this.changeHelpPanelVisibility();
  }

  public getFileWord(): string {
    return this.files.length > 1 ? this.translate.instant('MENU.PORTAL.HELP.FILE') : this.translate.instant('MENU.PORTAL.HELP.FILES');
  }

  public getOutstandingFiles(): number {
    return (this.ALLOWED_ATTACHMENTS - (this.files?.length || 0))
  }

  public ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  public cancelHelp() {
    this.changeHelpPanelVisibility();
    this.clearHelpWidget();
  }


  public displayErrorMsg(): void {
    setTimeout(() => { this.isErrorMsgVisible = false; }, this.ERROR_MSG_DISPLAY_TIME);
  }

  public toggleTextAnimation(isHovered: boolean): void {
    this.textState = isHovered ? 'visible' : 'hidden';
  }

  private prepareFeedbackData(): HelpRequest {
    return {
      text: this.feedbackForm.value.text,
      clientName: this.currentClient.name,
      url: this.router.url,
      userEmail: this.userProfile.email,
      userAgentInformation: this.helpService.getUserInformation(),
    }
  }

  private clearHelpWidget(): void {
    this.feedbackForm.reset();
    this.files = [];
  }
}
